import { Injectable } from '@angular/core';
import { AppConfig } from '../app.config';
import { environment } from '../../environments/environment';
import { Baseline, StudentOutcome, SubmissionDataEntity } from '../_models/index';
import { ActionData } from '../_models/actions';
import { SearchPlacementRequest } from '../_models/placement-search/search-placement-request.model';
import { PagedRequest } from '../_models/common/paged-response.model';
import { PlacementMaintenance } from '../_models/maintenance/placement-maintenance.model';
import { catchError, map } from 'rxjs/operators';
import { BaseService } from './base/base.service';
import { SessionUserHashManagerService } from './session-user-hash-manager/session-user-hash-manager.service';
import { HttpClient } from '@angular/common/http';
import { LaunchDigitalClassroom } from '../_models/digital-classroom/digital-classroom-launch-session.model';
import { LaunchVideoLibrary } from '../_models/digital-classroom/digital-classroom-launch-library.model';
import { LaunchSessionOverview } from '../_models/digital-classroom/digital-classroom-launch-overview.model';

@Injectable()
export class PlacementService extends BaseService {
  
  private apiUrl: string = environment.apiUrl;

  constructor(
    private http: HttpClient, 
    private config: AppConfig,
    protected hashManagerService: SessionUserHashManagerService
    ) {
    super(hashManagerService);
    this.apiUrl += this.apiUrl.endsWith("/") ? "" : "/";
  }

  //TODO go to api for time
  getDateTimeNow() {
    return this.http.get(this.apiUrl + 'api/TutorReport/GetDateTimeNow', this.prepareOptions(null)).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('getDateTimeNow'))
    );
  }



  getAll() {
    return this.http.get(this.apiUrl + 'api/TutorReport', this.prepareOptions(null)).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('getAll'))
    );
  }

  /** Every caller is a page loading the booking in its URL, so not found reads as one plain message (40973). */
  getById(id: number) {
    return this.http.get(this.apiUrl + 'api/TutorReport/GetPlacementById/' + id, this.prepareBookingLoadOptions(null)).pipe(
      map((data: any) => {

        return data.body;
      }), catchError(this.handleBookingLoadError('getById'))
    );
  }

  saveBaseline(baseline: Baseline) {
    return this.http.post(this.apiUrl + 'api/TutorReport/CreateBaseline/', baseline, this.prepareOptions(baseline)).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('saveBaseline'))
    );
  }

  getBaseline(placementId: number, studentId: number, isStudentGroup: boolean) {
    return this.http.post(this.apiUrl + 'api/TutorReport/ReadBaseline/', 
    {"PlacementId": placementId, "StudentId": studentId, "IsStudentGroup": isStudentGroup}, 
    this.prepareOptions({"PlacementId": placementId, "StudentId": studentId, "IsStudentGroup": isStudentGroup})).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('getBaseline'))
    );
  }

  submitBaseline(baseline: Baseline) {
    return this.http.post(this.apiUrl + 'api/TutorReport/SubmitBaseline/', baseline, this.prepareOptions(baseline)).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('submitBaseline'))
    );
  }

  getStudentOutcomes(placementId: number, studentId: number, isStudentGroup: boolean) {
    return this.http.post(this.apiUrl + 'api/TutorReport/ReadStudentOutcomes/',
    {"PlacementId": placementId, "StudentId": studentId, "IsStudentGroup": isStudentGroup}, 
    this.prepareOptions({"PlacementId": placementId, "StudentId": studentId, "IsStudentGroup": isStudentGroup})).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('getStudentOutcomes'))
    );
  }

  getStudentOutcome(id: number) {
    return this.http.get(this.apiUrl + 'api/TutorReport/getstudentOutcome/' + id, this.prepareOptions(null)).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('getStudentOutcome'))
    );
  }

  saveStudentOutcome(outcome: StudentOutcome) {
    return this.http.post(this.apiUrl + 'api/TutorReport/SaveStudentOutcome/', outcome, this.prepareOptions(outcome)).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('saveStudentOutcome'))
    );
  }

  submitStudentOutcome(outcome: StudentOutcome, action: ActionData) {
    return this.http.post(this.apiUrl + 'api/TutorReport/SubmitStudentOutcomeAction/', 
    { "StudentOutcome": outcome, "Action": action }, 
    this.prepareOptions({ "StudentOutcome": outcome, "Action": action })).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('submitStudentOutcome'))
    );
  }

  deleteOutcome(id: number) {
    return this.http.post(this.apiUrl + 'api/TutorReport/DeleteOutcome', {"Id": id}, 
    this.prepareOptions({"Id": id})).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('deleteOutcome'))
    );
  }

  deleteSession(id: number) {
    return this.http.post(this.apiUrl + 'api/TutorReport/DeleteSession', {"Id": id}, this.prepareOptions({"Id": id})).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('deleteSession'))
    );
  }

  deleteBaselineService(placementId: number, studentId: number) {
    let requestObject = {};
    if (studentId == null) {
      requestObject = {"PlacementId": placementId}
    } else {
      requestObject = {"PlacementId": placementId, "StudentId": studentId}
    }
    return this.http.post(this.apiUrl + 'api/TutorReport/DeleteBaseline/', requestObject,
    this.prepareOptions(requestObject)).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('deleteBaselineService'))
    );
  }

  getStudentSessions(placementId: number, studentId: number, isStudentGroup: boolean) {
    let requestObject = {};
    if (studentId == null) {
      requestObject = {"PlacementId": placementId, "IsStudentGroup": isStudentGroup}
    } else {
      requestObject = {"PlacementId": placementId, "StudentId": studentId, "IsStudentGroup": isStudentGroup}
    }
    return this.http.post(this.apiUrl + 'api/TutorReport/ReadStudentSessions/',
    requestObject, 
    this.prepareOptions(requestObject)).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('getStudentSessions'))
    );
  }

  getStudentSessionExternalData(data) {
    return this.http.post(this.apiUrl + 'api/TutorReport/getStudentSessionExternalData', data,  this.prepareOptions(data)).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('getStudentSessionExternalData'))
    );
  }

  saveStudentSession(data) {
    return this.http.post(this.apiUrl + 'api/TutorReport/SaveStudentSession/', data, this.prepareOptions(data)).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('saveStudentSession'))
    );
  }

  submitStudentSession(data) {
    return this.http.post(this.apiUrl + 'api/TutorReport/submitStudentSession/', data, this.prepareOptions(data)).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('submitStudentSession'))
    );
  }

  RejectTutorReport(data) {
    return this.http.post(this.apiUrl + 'api/TutorReport/RejectTutorReport/', data, this.prepareOptions(data)).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('RejectTutorReport'))
    );
  }

  ApproveTutorReport(data) {
    return this.http.post(this.apiUrl + 'api/TutorReport/ApproveTutorReport/', data, this.prepareOptions(data)).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('ApproveTutorReport'))
    );
  }

  ApproveBaseline(data) {
    return this.http.post(this.apiUrl + 'api/TutorReport/ApproveBaseline/', data, this.prepareOptions(data)).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('ApproveBaseline'))
    );
  }

  ApproveOutcome(data) {
    return this.http.post(this.apiUrl + 'api/TutorReport/ApproveOutcome/', data, this.prepareOptions(data)).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('ApproveOutcome'))
    );
  }

  ApproveSession(data) {
    return this.http.post(this.apiUrl + 'api/TutorReport/ApproveSession/', data, this.prepareOptions(data)).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('ApproveSession'))
    );
  }

  RejectBaseline(data: SubmissionDataEntity) {
    return this.http.post(this.apiUrl + 'api/TutorReport/RejectBaseline/', data, this.prepareOptions(data)).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('RejectBaseline'))
    );
  }

  RejectOutcome(data) {
    return this.http.post(this.apiUrl + 'api/TutorReport/RejectOutcome/', data, this.prepareOptions(data)).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('RejectOutcome'))
    );
  }

  RejectSession(rowId) {
    return this.http.post(this.apiUrl + 'api/TutorReport/RejectSession/', rowId, this.prepareOptions(rowId)).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('RejectSession'))
    );
  }

  GetSubmissionHistory(data) {
    return this.http.post(this.apiUrl + 'api/TutorReport/GetSubmissionHistory/', data, this.prepareOptions(data)).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('GetSubmissionHistory'))
    );
  }

  GetCrmUserAccounts() {
    return this.http.get(this.apiUrl + 'api/TutorReport/GetUserAccounts/', this.prepareOptions(null)).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('GetCrmUserAccounts'))
    );
  }

  GetCrmUserAccountsByPlacementId(relationId, placementId) {
    return this.http.get(this.apiUrl + 'api/TutorReport/GetUserAccounts?relationshipId=' + relationId + "&placementId=" + placementId, this.prepareOptions(null)).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('GetCrmUserAccountsByPlacementId'))
    );
  }

  GetBaselineFrame(placementId: number, studentId: number = 0, isStudentGroup: boolean = false) {
    let payload = {
      "placementId": placementId,
      "studentId": studentId,
      "isStudentGroup": isStudentGroup
    }
    return this.http.post(this.apiUrl + 'api/TutorReport/GetIndividualLearningPlanFrame/', payload, this.prepareOptions(payload)).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('GetBaselineFrame'))
    );
  }

  GetUsersByPlacementIds(data) {
    return this.http.post(this.apiUrl + 'api/TutorReport/GetUsersByPlacementIds/', data, this.prepareOptions(data)).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('GetUsersByPlacementIds'))
    );
  }


  GetTargetEngagementGrades() {
    return this.http.get(this.apiUrl + 'api/TutorReport/GetTargetEngagementGrades/', this.prepareOptions(null)).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('GetTargetEngagementGrades'))
    );
  }

  /**
   * Searches placements. `paging` is opt-in: omit it (the default) to get today's full, unpaged
   * array - `view-student-bookings.component.ts` and both `session-overview` call sites rely on
   * this and must keep calling with no second argument. Pass it only from
   * `placement-search.component.ts`, the only caller converted to server-side paging
   * (Plans/table-lazy-loading.md §4 A1) - it then receives a `PagedResponse<SearchPlacementResult>`
   * instead of the bare array.
   */
  searchPlacements(request: SearchPlacementRequest, paging?: PagedRequest) {
    if (paging) {
      request.paged = true;
      request.pageNumber = paging.pageNumber;
      request.pageSize = paging.pageSize;
      request.sortField = paging.sortField;
      request.sortDirection = paging.sortDirection;
    }
    return this.http.post(this.apiUrl + 'api/TutorReport/RunPlacementSearch/', request, this.prepareOptions(request)).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('searchPlacements'))
    );
  }

  /** loadingPage: the page is loading the booking in its URL, so not found reads as one plain message (40973). */
  getPlacementMaintenanceInfo(placementId: string, loadingPage: boolean = false) {
    const payload = { "Id": placementId };
    const options = loadingPage ? this.prepareBookingLoadOptions(payload) : this.prepareOptions(payload);
    const handle = loadingPage ? this.handleBookingLoadError('updatePlacementMaintenanceInfo') : this.handleError('updatePlacementMaintenanceInfo');
    return this.http.post(this.apiUrl + 'api/TutorReport/PlacementMaintenance', payload, options).pipe(
      map((data: any) => {

        return data.body;
      }), catchError(handle)
    );
  }

  updatePlacementMaintenanceInfo(request: PlacementMaintenance) {
    return this.http.post(this.apiUrl + 'api/TutorReport/PlacementMaintenanceUpdate/', request, this.prepareOptions(request)).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('updatePlacementMaintenanceInfo'))
    );
  }

  unlockProgressReport(progressReportId: number, studentId: number) {
    return this.http.post(this.apiUrl + 'api/TutorReport/UnlockProgressReport/', { "Id": progressReportId, "StudentId": studentId }, this.prepareOptions({ "Id": progressReportId, "StudentId": studentId })).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('unlockProgressReport'))
    );
  }

  unlockBaseline(placementId: number, studentId: number) {
    return this.http.post(this.apiUrl + 'api/TutorReport/UnlockILP/', { "Id": placementId, "StudentId": studentId }, this.prepareOptions({ "Id": placementId, "StudentId": studentId })).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('unlockBaseline'))
    );
  }

  launchDigitalClassroom(request: LaunchDigitalClassroom) {
    return this.http.post(this.apiUrl + 'api/TutorReport/LaunchDigitalClassroom', request, this.prepareOptions(request)).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('launchDigitalClassroom'))
    );
  }

  /**
   * Sets up the Digital Classroom for a booking Orbit has as in person, so a session delivered online can run there
   * (39883), and switches orbitSessionId (the session the classroom launches for next) to Online. The API answers 404
   * while the sessions.delivery-setting switch is off.
   */
  offerOnlineClassroom(placementId: number, orbitSessionId: number | null = null) {
    const request = { id: placementId, orbitSessionId };
    return this.http.post(this.apiUrl + 'api/TutorReport/OfferOnlineClassroom', request, this.prepareOptions(request)).pipe(
      map((data: any) => data.body),
      catchError(this.handleError('offerOnlineClassroom'))
    );
  }

  launchVideoLibrary(request: LaunchVideoLibrary) {
    return this.http.post(this.apiUrl + 'api/TutorReport/LaunchVideoLibrary', request, this.prepareOptions(request)).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('launchVideoLibrary'))
    );
  }

  launchOnlineOverview(request: LaunchSessionOverview) {
    return this.http.post(this.apiUrl + 'api/TutorReport/LaunchOnlineOverview', request, this.prepareOptions(request)).pipe(
      map((data: any) => {
        
        return data.body;
      }), catchError(this.handleError('launchVideoLibrary'))
    );
  }

  GetTotalRecentAbsences(request: any) {
    return this.http.post(this.apiUrl + 'api/TutorReport/GetTotalRecentAbsences', request, this.prepareOptions(request)).pipe( map((data: any) => {        
      return data.body;
    }), catchError(this.handleError('launchVideoLibrary')));
  }

}
